// Small shared pieces for the Finance & Books screens.
// Everything here is mobile-first: tables live inside horizontal scrollers and
// forms stack to one column at 375px.
import { Loader2 } from "lucide-react";

import { fmtMinor, tone } from "../../lib/books";

export function Section({ title, description, actions, children, testId }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={testId}>
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <div>
            {title && <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>}
            {description && <p className="text-sm text-slate-600 mt-0.5 max-w-2xl">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Stat({ label, value, hint, testId, tone: toneClass = "text-slate-900" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export function Money({ minor, className = "" }) {
  const negative = Number(minor || 0) < 0;
  return (
    <span className={`tabular-nums ${negative ? "text-rose-600" : ""} ${className}`}>
      {fmtMinor(minor)}
    </span>
  );
}

export function Pill({ children, status }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone(status)}`}>
      {children}
    </span>
  );
}

export function Table({ head, children, empty, testId }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full min-w-[540px] text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">
            {head.map((h) => (
              <th key={h.key || h} className={`py-2 pr-3 font-semibold ${h.align === "right" ? "text-right" : ""}`}>
                {h.label || h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
      {empty}
    </div>
  );
}

export function Empty({ children, testId }) {
  return (
    <p className="py-6 text-center text-sm text-slate-500" data-testid={testId}>{children}</p>
  );
}

export function Field({ label, children, hint, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </label>
  );
}

export const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none";

export function Button({ children, variant = "primary", className = "", busy, ...rest }) {
  const styles = {
    primary: "bg-slate-900 text-white hover:bg-slate-800",
    secondary: "border border-slate-300 bg-white text-slate-800 hover:bg-slate-50",
    danger: "border border-rose-300 bg-white text-rose-700 hover:bg-rose-50",
  }[variant];
  return (
    <button
      {...rest}
      disabled={rest.disabled || busy}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium
                  disabled:opacity-50 disabled:cursor-not-allowed ${styles} ${className}`}
    >
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Modal({ title, description, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-0 sm:p-4"
         role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-semibold">{title}</h3>
            {description && <p className="text-sm text-slate-600 mt-0.5">{description}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-sm" aria-label="Close"
                  data-testid="books-modal-close">
            Close
          </button>
        </div>
        <div className="mt-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

export function Loading({ label = "Loading..." }) {
  return (
    <div className="flex items-center gap-2 py-8 justify-center text-sm text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin" /> {label}
    </div>
  );
}
